<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import CallsAPI from 'dashboard/api/calls';
import { extensionFromUrl } from 'dashboard/helper/URLHelper';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import AudioChip from 'next/message/chips/Audio.vue';
import CreateCallLinkDialog from './CreateCallLinkDialog.vue';

const { t } = useI18n();

const createLinkDialogRef = ref(null);

const calls = ref([]);
const meta = ref({ current_page: 1, total_pages: 1, count: 0 });
const isLoading = ref(false);
const hasError = ref(false);
const expandedId = ref(null);

const formatDuration = seconds => {
  if (!seconds) return '—';
  const mins = Math.floor(seconds / 60);
  const secs = String(seconds % 60).padStart(2, '0');
  return `${mins}:${secs}`;
};

const formatDate = unix => {
  if (!unix) return '—';
  return new Date(unix * 1000).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const statusClass = status => {
  if (status === 'completed') return 'bg-n-brand/10 text-n-brand';
  if (['failed', 'no-answer', 'rejected'].includes(status)) {
    return 'bg-n-ruby-4 text-n-ruby-11';
  }
  return 'bg-n-slate-3 text-n-slate-11';
};

const fetchCalls = async (page = 1) => {
  isLoading.value = true;
  hasError.value = false;
  try {
    const { data } = await CallsAPI.getCalls({ page });
    calls.value = data.payload || [];
    meta.value = data.meta || meta.value;
  } catch (error) {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

const goToPage = page => {
  if (page < 1 || page > meta.value.total_pages) return;
  fetchCalls(page);
};

const toggleTranscript = id => {
  expandedId.value = expandedId.value === id ? null : id;
};

// ATTACHMENT_TYPES.AUDIO, inlined: importing the message bubble's constants
// module here would pull its whole (unrelated) enum set into this page.
const recordingAttachment = call => ({
  dataUrl: call.recording_url,
  fileType: 'audio',
  extension: extensionFromUrl(call.recording_url),
});

onMounted(() => fetchCalls());
</script>

<template>
  <section
    class="flex flex-col w-full h-full min-w-0 gap-3 p-3 overflow-hidden bg-n-background"
  >
    <div
      class="flex flex-col flex-1 min-h-0 overflow-hidden shadow-sm bg-n-surface-1 rounded-[24px]"
    >
      <header
        class="flex items-center justify-between flex-shrink-0 px-6 py-4 border-b border-n-weak"
      >
        <div>
          <h1 class="font-caprasimo text-2xl leading-none text-n-slate-12">
            {{ t('CALL_HISTORY.TITLE') }}
          </h1>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ t('CALL_HISTORY.SUBTITLE', { count: meta.count }) }}
          </p>
        </div>
        <NextButton
          sm
          icon="i-lucide-link"
          :label="t('CALL_HISTORY.CREATE_LINK.BUTTON')"
          @click="createLinkDialogRef?.open()"
        />
      </header>

      <div class="flex-1 min-h-0 overflow-y-auto">
        <!-- Loading -->
        <div
          v-if="isLoading"
          class="flex items-center justify-center py-20 text-n-brand"
        >
          <Spinner />
        </div>

        <!-- Error -->
        <div
          v-else-if="hasError"
          class="flex flex-col items-center gap-3 py-20 text-center"
        >
          <p class="text-sm text-n-slate-11">{{ t('CALL_HISTORY.ERROR') }}</p>
          <NextButton
            sm
            slate
            faded
            :label="t('CALL_HISTORY.RETRY')"
            @click="fetchCalls(meta.current_page)"
          />
        </div>

        <!-- Empty -->
        <div
          v-else-if="!calls.length"
          class="flex flex-col items-center gap-2 py-20 text-center"
        >
          <span class="i-lucide-phone-off size-8 text-n-slate-9" />
          <p class="text-sm text-n-slate-11">{{ t('CALL_HISTORY.EMPTY') }}</p>
        </div>

        <!-- List with column header -->
        <div v-else class="flex flex-col">
          <div
            class="sticky top-0 z-10 flex items-center gap-4 px-7 py-2.5 bg-n-surface-1 border-b border-n-weak text-xxs font-medium uppercase tracking-wider text-n-slate-10"
          >
            <span class="flex-shrink-0 size-9" aria-hidden="true" />
            <span class="flex-1 min-w-0">{{
              t('CALL_HISTORY.COL_CUSTOMER')
            }}</span>
            <span class="hidden w-40 md:block">{{
              t('CALL_HISTORY.COL_AGENT')
            }}</span>
            <span class="hidden w-14 sm:block">{{
              t('CALL_HISTORY.COL_DURATION')
            }}</span>
            <span class="hidden w-28 lg:block">{{
              t('CALL_HISTORY.COL_DATE')
            }}</span>
            <span class="flex-shrink-0 w-16">{{
              t('CALL_HISTORY.COL_STATUS')
            }}</span>
          </div>
          <ul class="flex flex-col gap-1 p-3 m-0 list-none">
            <li
              v-for="call in calls"
              :key="call.id"
              class="rounded-[18px] hover:bg-n-alpha-1 transition-colors"
            >
              <div class="flex items-center gap-4 px-4 py-3 min-w-0">
                <!-- direction -->
                <span
                  class="grid flex-shrink-0 rounded-full size-9 place-content-center"
                  :class="
                    call.direction === 'inbound'
                      ? 'bg-n-brand/10 text-n-brand'
                      : 'bg-[rgb(var(--organic-accent-2))]/15 text-[rgb(var(--organic-accent-2))]'
                  "
                >
                  <span
                    :class="
                      call.direction === 'inbound'
                        ? 'i-lucide-phone-incoming'
                        : 'i-lucide-phone-outgoing'
                    "
                    class="size-4"
                  />
                </span>

                <!-- customer -->
                <div class="flex items-center gap-2.5 min-w-0 flex-1">
                  <Avatar
                    :name="call.contact?.name || t('CALL_HISTORY.UNKNOWN')"
                    :src="call.contact?.avatar"
                    :size="32"
                  />
                  <div class="min-w-0">
                    <p class="text-sm font-medium truncate text-n-slate-12">
                      {{ call.contact?.name || t('CALL_HISTORY.UNKNOWN') }}
                    </p>
                    <p class="text-xs truncate text-n-slate-11">
                      {{ call.contact?.phone_number || '—' }}
                    </p>
                  </div>
                </div>

                <!-- agent -->
                <div class="items-center hidden gap-2 min-w-0 w-40 md:flex">
                  <template v-if="call.agent">
                    <Avatar
                      :name="call.agent.name"
                      :src="call.agent.avatar"
                      :size="24"
                    />
                    <span class="text-sm truncate text-n-slate-11">
                      {{ call.agent.name }}
                    </span>
                  </template>
                  <span v-else class="text-sm text-n-slate-10">
                    {{ t('CALL_HISTORY.NO_AGENT') }}
                  </span>
                </div>

                <!-- duration -->
                <span
                  class="hidden text-sm tabular-nums text-n-slate-11 w-14 sm:inline-block"
                >
                  {{ formatDuration(call.duration_seconds) }}
                </span>

                <!-- date -->
                <span
                  class="hidden text-sm text-n-slate-11 w-28 lg:inline-block"
                >
                  {{ formatDate(call.started_at || call.created_at) }}
                </span>

                <!-- status -->
                <span
                  class="px-2.5 py-1 text-xxs font-medium rounded-full capitalize flex-shrink-0"
                  :class="statusClass(call.status)"
                >
                  {{ call.status }}
                </span>

                <!-- transcript toggle -->
                <NextButton
                  v-if="call.transcript"
                  xs
                  slate
                  ghost
                  icon="i-lucide-file-text"
                  :title="t('CALL_HISTORY.TRANSCRIPT')"
                  @click="toggleTranscript(call.id)"
                />
              </div>

              <!-- recording + transcript detail -->
              <div
                v-if="call.recording_url || expandedId === call.id"
                class="flex flex-col gap-3 px-4 pb-4 ltr:pl-[68px] rtl:pr-[68px]"
              >
                <AudioChip
                  v-if="call.recording_url"
                  class="w-full max-w-md"
                  :attachment="recordingAttachment(call)"
                  :show-transcribed-text="false"
                />
                <div
                  v-if="expandedId === call.id"
                  class="p-3 text-sm whitespace-pre-wrap rounded-xl bg-n-alpha-1 text-n-slate-11"
                >
                  {{ call.transcript }}
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>

      <!-- pagination -->
      <footer
        v-if="!isLoading && meta.total_pages > 1"
        class="flex items-center justify-between flex-shrink-0 px-6 py-3 border-t border-n-weak"
      >
        <span class="text-xs text-n-slate-11">
          {{
            t('CALL_HISTORY.PAGE', {
              current: meta.current_page,
              total: meta.total_pages,
            })
          }}
        </span>
        <div class="flex gap-2">
          <NextButton
            sm
            slate
            faded
            icon="i-lucide-chevron-left"
            :disabled="meta.current_page <= 1"
            @click="goToPage(meta.current_page - 1)"
          />
          <NextButton
            sm
            slate
            faded
            icon="i-lucide-chevron-right"
            :disabled="meta.current_page >= meta.total_pages"
            @click="goToPage(meta.current_page + 1)"
          />
        </div>
      </footer>
    </div>
    <CreateCallLinkDialog ref="createLinkDialogRef" />
  </section>
</template>
